import { FC, useEffect, useState } from 'react';
import _ from 'lodash';
import KeywordForm from '../KeywordForm';
import DisplayForm from '../DisplayForm';
import ShopForm from '../ShopForm';
import RecommendForm from '../RecommendForm';
import { AdType, RtbCampaign } from 'core/rtbCampaign/RtbCampaign';
import { useCampaignStore } from 'store/campaignStore';
import { useProductStore } from 'store/productStore';
import useCreativeStore from 'store/creativeStore';
import { Redirect, useParams } from 'react-router-dom';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager,
} from 'core/rtbCampaign/RtbCampaignManager';
import {
  CreativeManager,
  DefaultCreativeManager,
} from 'core/creative/CreativeManager';
import { SelectPattern } from 'core/rtbCampaign/singlePage/common';
import { CampaignBannerMapState } from 'core/creative/Creative';
import { Product } from 'core/product/Product';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { ROUTE_PATH } from 'enum/RoutePath';
import { useOrderStore } from 'store/orderStore';
import OutboundKeywordForm from '../OutboundKeywordForm';

const defaultCampaignManager: RtbCampaignManager =
  new DefaultRtbCampaignManager();
const creativeManager: CreativeManager = new DefaultCreativeManager();

const EditPage: FC = () => {
  const { order } = useOrderStore();
  const { handleSetCampaign } = useCampaignStore();
  const { handleAddCreatives } = useCreativeStore();
  const { resetProductStore } = useProductStore();
  const [defaultCampaign, setCampaign] = useState<RtbCampaign>(
    {} as RtbCampaign,
  );
  const params = useParams();

  const formType = () => {
    switch (defaultCampaign?.basic?.adType) {
      case AdType.KEYWORD:
        return <KeywordForm defaultForm={defaultCampaign} />;
      case AdType.SMART_KEYWORD:
        return <KeywordForm defaultForm={defaultCampaign} />;
      case AdType.MAX_CVR_KEYWORD:
        return <KeywordForm defaultForm={defaultCampaign} />;
      case AdType.SHOP:
        return <ShopForm defaultForm={defaultCampaign} />;
      case AdType.DISPLAY:
        return <DisplayForm defaultForm={defaultCampaign} />;
      case AdType.RECOMMEND:
        if (defaultCampaign.basic?.selectPattern !== SelectPattern.AUTO) {
          return (
            <Redirect
              push
              to={{
                pathname: `/orders/${order.orderNumber}/${ROUTE_PATH.ERROR403}`,
                state: { message: 'canViewAllAccounts test failed' },
              }}
            />
          );
        } else {
          return <RecommendForm defaultForm={defaultCampaign} />;
        }
      case AdType.OUTBOUND_KEYWORD:
        return <OutboundKeywordForm defaultForm={defaultCampaign} />;
      default:
        return '';
    }
  };

  const updateCampaignProducts = (creatives: any, key: string) => {
    return _.compact(
      creatives
        .filter(
          (creative) =>
            creative.bindingState !== CampaignBannerMapState.DISABLE,
        )
        .map((creative) => creative[key]),
    );
  };

  useEffect(() => {
    const fetchCreative = async (campaignId: string, camPaign: RtbCampaign) => {
      try {
        if (campaignId) {
          const creatives = await creativeManager.getCreativesByCampaignId(
            campaignId,
            false,
          );
          if (camPaign.basic.adType === AdType.SHOP) {
            handleAddCreatives(
              creatives.map((creative) => ({
                name: creative.name,
                id: creative.id,
                creativeType: creative.creativeType,
                brandName: creative.creativeValue.brandName,
                webBrandNo: creative.creativeValue.webBrandNo,
                categoryCode: creative.creativeValue.categoryCode,
                categoryName: creative.creativeValue.categoryName,
                bannerUrl: creative.creativeValue.bannerUrl,
                productName: creative.creativeValue.productName,
                productId: creative.creativeValue.productId,
                bindingState: creative.bindingState,
              })),
            );
          } else {
            handleAddCreatives(creatives);
          }
          if (camPaign.basic?.selectPattern === SelectPattern.AUTO) {
            useCampaignStore.setState({ lockBrand: true });
            if (creatives.length > 0) {
              setCampaign({
                ...camPaign,
                autoProducts: updateCampaignProducts(
                  creatives,
                  'product',
                ) as Product[],
              });
              useProductStore.setState({
                selectedAutoProducts: updateCampaignProducts(
                  creatives,
                  'product',
                ) as Product[],
              });
            } else {
              setCampaign({
                ...camPaign,
              });
            }
          } else {
            if (
              creatives &&
              [
                AdType.KEYWORD,
                AdType.SMART_KEYWORD,
                AdType.MAX_CVR_KEYWORD,
                AdType.RECOMMEND,
                AdType.OUTBOUND_KEYWORD,
              ].includes(camPaign?.basic?.adType as AdType)
            ) {
              setCampaign({
                ...camPaign,
                products: updateCampaignProducts(
                  creatives,
                  'product',
                ) as Product[],
              });
              useProductStore.setState({
                selectedProducts: updateCampaignProducts(
                  creatives,
                  'product',
                ) as Product[],
              });
            } else {
              setCampaign({
                ...camPaign,
              });
            }
          }
          // 寫入campaign到store
          handleSetCampaign({
            ...camPaign,
          });
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (params['campaignId']) {
      const fetchCampaign = async (campaignId: string) => {
        try {
          if (campaignId) {
            const campaign =
              await defaultCampaignManager.getCampaign(campaignId);
            useCampaignStore.setState({ adType: campaign.basic.adType });

            if (campaign.basic && !campaign.basic.bidMode) {
              campaign.basic.bidMode = SelectPattern.AUTO;
            }
            fetchCreative(campaignId, campaign);
          }
        } catch (e) {
          console.error(e);
        }
      };
      fetchCampaign(params['campaignId']);
    }
    return () => {
      handleAddCreatives([]);
      resetProductStore();
    };
  }, []);

  if (_.isEmpty(defaultCampaign)) {
    return <LoadingIndicator />;
  }
  return <>{!_.isEmpty(defaultCampaign) && formType()}</>;
};
export default EditPage;
